import {
  askCoachResponseSchema,
  staffParentWantedResponseSchema,
  type AnswerParentWantedRequest,
  type StaffParentWantedResponse,
  askParentsResponseSchema,
  findChildrenResponseSchema,
  findClaimResponseSchema,
  findPageSchema,
  linkParentClaimResponseSchema,
  type FindChild,
  type FindClaimRequest,
  type FindPage,
  cleanGroupNamesResponseSchema,
  type CleanGroupNamesResponse,
  nameGroupPeopleResponseSchema,
  type NameGroupPeopleRequest,
  type NameGroupPeopleResponse,
  addSessionExtraResponseSchema,
  ownerSessionExtrasResponseSchema,
  cameDuringPauseResponseSchema,
  sessionExtraSearchResponseSchema,
  sessionNotBookedResponseSchema,
  type AddSessionExtraRequest,
  type AddSessionExtraResponse,
  type DecideSessionExtraRequest,
  type AnswerCameDuringPauseRequest,
  type CameDuringPauseResponse,
  type OwnerSessionExtrasResponse,
  type SessionExtraSearchResponse,
  type SessionNotBookedResponse,
  addStaffParticipantsResponseSchema,
  linkNewParticipantResponseSchema,
  ownerNewParticipantsResponseSchema,
  runParentMatchResponseSchema,
  unlinkNewParticipantResponseSchema,
  importedContactSchema,
  type RunParentMatchResponse,
  type UnlinkNewParticipantResponse,
  sendStaffAskResponseSchema,
  staffAskResponseSchema,
  staffParticipantsDoneResponseSchema,
  staffParticipantsResponseSchema,
  staffWaitingSchema,
  type AddStaffParticipantsRequest,
  type AddStaffParticipantsResponse,
  type ClaimStaffParticipantRequest,
  type LinkNewParticipantRequest,
  type LinkNewParticipantResponse,
  type OwnerNewParticipantsResponse,
  type SendStaffAskRequest,
  type SendStaffAskResponse,
  type StaffAskResponse,
  type StaffParticipantsDoneResponse,
  type StaffParticipantsResponse,
  type StaffWaiting,
  type UpdateStaffParticipantRequest,
} from "@teamna/shared";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { okSchema as okResponseSchema } from "@teamna/shared";
import { api, newIdempotencyKey } from "../api/client.ts";
import { businessScheduleQueries, notBookedQueries, queryKeys } from "./keys.ts";

/**
 * Coaches add their own swimmers (strategy repo: teamna/prototypes/coach-adds-swimmers.html).
 * The coach's list (`/staff/participants`), the owner's list of names to link
 * (`/new-participants`), and the "Ask coaches" card on the Team page (`/staff-requests`).
 */

const base = (businessId: string) => `/business/businesses/${businessId}`;

/** A coach's change moves the owner's list, the families, and the counts on Home. */
export function refreshAll(queryClient: QueryClient, businessId: string): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.coachParticipants.all(businessId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.setup.all(businessId) });
  void queryClient.invalidateQueries(notBookedQueries);
}

// --- the coach ----------------------------------------------------------------

/** GET /business/businesses/:id/staff/participants */
export function useStaffParticipants(businessId: string | null): UseQueryResult<StaffParticipantsResponse> {
  return useQuery({
    queryKey: queryKeys.coachParticipants.mine(businessId ?? ""),
    queryFn: () => api.get(`${base(businessId!)}/staff/participants`, staffParticipantsResponseSchema),
    enabled: Boolean(businessId),
  });
}

export function useAddStaffParticipants(
  businessId: string,
): UseMutationResult<AddStaffParticipantsResponse, unknown, AddStaffParticipantsRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      api.post(`${base(businessId)}/staff/participants`, input, addStaffParticipantsResponseSchema, {
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: () => refreshAll(queryClient, businessId),
  });
}

/** "Same swimmer". */
export function useClaimStaffParticipant(businessId: string): UseMutationResult<StaffWaiting, unknown, ClaimStaffParticipantRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => api.post(`${base(businessId)}/staff/participants/same`, input, staffWaitingSchema),
    onSuccess: () => refreshAll(queryClient, businessId),
  });
}

export function useUpdateStaffParticipant(
  businessId: string,
): UseMutationResult<StaffWaiting, unknown, UpdateStaffParticipantRequest & { id: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }) => api.patch(`${base(businessId)}/staff/participants/${id}`, input, staffWaitingSchema),
    onSuccess: (row) => {
      // The saved row goes into the list at once, so a note opened again before the refresh shows
      // what was saved.
      queryClient.setQueryData<StaffParticipantsResponse>(queryKeys.coachParticipants.mine(businessId), (old) =>
        old ? { ...old, levels: old.levels.map((level) => ({ ...level, waiting: level.waiting.map((entry) => (entry.id === row.id ? row : entry)) })) } : old,
      );
      refreshAll(queryClient, businessId);
    },
  });
}

export function useRemoveStaffParticipant(businessId: string): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`${base(businessId)}/staff/participants/${id}`),
    onSuccess: () => refreshAll(queryClient, businessId),
  });
}

/** "Done": the owner gets one message for the open batch. */
export function useFinishStaffBatch(businessId: string): UseMutationResult<StaffParticipantsDoneResponse, unknown, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`${base(businessId)}/staff/participants/done`, {}, staffParticipantsDoneResponseSchema),
    onSuccess: () => refreshAll(queryClient, businessId),
  });
}

// --- the owner ----------------------------------------------------------------

/** GET /business/businesses/:id/new-participants */
export function useNewParticipants(businessId: string | null): UseQueryResult<OwnerNewParticipantsResponse> {
  return useQuery({
    queryKey: queryKeys.coachParticipants.owner(businessId ?? ""),
    queryFn: () => api.get(`${base(businessId!)}/new-participants`, ownerNewParticipantsResponseSchema),
    enabled: Boolean(businessId),
  });
}

export function useLinkNewParticipant(
  businessId: string,
): UseMutationResult<LinkNewParticipantResponse, unknown, LinkNewParticipantRequest & { id: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }) =>
      api.post(`${base(businessId)}/new-participants/${id}/link`, input, linkNewParticipantResponseSchema, {
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: () => {
      refreshAll(queryClient, businessId);
      void queryClient.invalidateQueries({ queryKey: queryKeys.owner.members(businessId) });
    },
  });
}

/** Unlink: a link made by mistake goes back, and the name waits again (link-parents-sync). */
export function useUnlinkNewParticipant(businessId: string): UseMutationResult<UnlinkNewParticipantResponse, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) =>
      api.post(`${base(businessId)}/new-participants/${id}/unlink`, {}, unlinkNewParticipantResponseSchema, { idempotencyKey: newIdempotencyKey() }),
    onSuccess: () => {
      refreshAll(queryClient, businessId);
      void queryClient.invalidateQueries({ queryKey: queryKeys.owner.members(businessId) });
    },
  });
}

/** Teamna reads the names again and finds the parents in the synced group. */
export function useRunParentMatches(businessId: string): UseMutationResult<RunParentMatchResponse, unknown, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`${base(businessId)}/new-participants/matches`, {}, runParentMatchResponseSchema),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.coachParticipants.owner(businessId) }),
  });
}

/** The AI reads the WhatsApp names of the group it did not read yet (step 1 asks once). */
export function useCleanGroupNames(businessId: string): UseMutationResult<CleanGroupNamesResponse, unknown, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`${base(businessId)}/new-participants/clean-names`, {}, cleanGroupNamesResponseSchema),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.coachParticipants.owner(businessId) }),
  });
}

/** "Not right" on a match. */
export function useDismissParentMatch(businessId: string): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      await api.post(`${base(businessId)}/new-participants/matches/${id}/dismiss`, {}, okResponseSchema);
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.coachParticipants.owner(businessId) }),
  });
}

/** "Name the numbers": the owner types a name for a person of the group. A sync never replaces it. */
export function useNameGroupPerson(businessId: string): UseMutationResult<void, unknown, { id: string; name: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, name }) => {
      await api.patch(`${base(businessId)}/imported-contacts/${id}`, { displayName: name }, importedContactSchema);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.coachParticipants.owner(businessId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.setup.all(businessId) });
    },
  });
}

/** POST /business/businesses/:id/new-participants/names: the names from a phone contacts file. */
export function useNameGroupPeople(businessId: string): UseMutationResult<NameGroupPeopleResponse, unknown, NameGroupPeopleRequest["names"]> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (names) => api.post(`${base(businessId)}/new-participants/names`, { names }, nameGroupPeopleResponseSchema),
    // Also after an error: the names saved before it show as saved (review F3.4).
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.coachParticipants.owner(businessId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.setup.all(businessId) });
    },
  });
}

/** "Not our swimmer". */
export function useRemoveNewParticipant(businessId: string): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`${base(businessId)}/new-participants/${id}`),
    onSuccess: () => refreshAll(queryClient, businessId),
  });
}

/** GET /business/businesses/:id/staff-requests: the "Ask coaches" card. */
export function useStaffAsk(businessId: string | null): UseQueryResult<StaffAskResponse> {
  return useQuery({
    queryKey: queryKeys.coachParticipants.ask(businessId ?? ""),
    queryFn: () => api.get(`${base(businessId!)}/staff-requests`, staffAskResponseSchema),
    enabled: Boolean(businessId),
  });
}

/** POST /business/businesses/:id/staff-requests: a WhatsApp and a push to each coach. */
export function useSendStaffAsk(businessId: string): UseMutationResult<SendStaffAskResponse, unknown, SendStaffAskRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      api.post(`${base(businessId)}/staff-requests`, input, sendStaffAskResponseSchema, { idempotencyKey: newIdempotencyKey() }),
    onSuccess: () => refreshAll(queryClient, businessId),
  });
}

// --- someone else came (slice 2) -------------------------------------------------

/** After an add, a remove or a decision: the session's roster, the schedules, both lists. */
function refreshExtras(queryClient: QueryClient, businessId: string): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.staff.all });
  void queryClient.invalidateQueries({ queryKey: queryKeys.owner.sessionsAll });
  void queryClient.invalidateQueries(businessScheduleQueries);
  void queryClient.invalidateQueries({ queryKey: queryKeys.owner.attendeesAll });
  void queryClient.invalidateQueries({ queryKey: queryKeys.memberHomeAll });
  refreshAll(queryClient, businessId);
}

/** GET .../sessions/:id/extras/search?q=. Runs from 2 letters. */
export function useSessionExtraSearch(businessId: string | null, sessionId: string, q: string): UseQueryResult<SessionExtraSearchResponse> {
  const typed = q.trim();
  return useQuery({
    queryKey: [...queryKeys.coachParticipants.all(businessId ?? ""), "extra-search", sessionId, typed],
    queryFn: () => api.get(`${base(businessId!)}/sessions/${sessionId}/extras/search`, sessionExtraSearchResponseSchema, { query: { q: typed } }),
    // No placeholder from the last query: a result for another name must never show under this one,
    // or its "Came today" would add the wrong swimmer (internal review F4.1).
    enabled: Boolean(businessId) && typed.length >= 2,
  });
}

/** GET .../sessions/:id/extras/not-booked: the level's swimmers who did not book this session. */
export function useSessionNotBooked(businessId: string | null, sessionId: string | null): UseQueryResult<SessionNotBookedResponse> {
  return useQuery({
    queryKey: queryKeys.owner.notBooked(businessId ?? "", sessionId ?? ""),
    queryFn: () => api.get(`${base(businessId!)}/sessions/${sessionId}/extras/not-booked`, sessionNotBookedResponseSchema),
    enabled: Boolean(businessId && sessionId),
  });
}

export function useAddSessionExtra(businessId: string, sessionId: string): UseMutationResult<AddSessionExtraResponse, unknown, AddSessionExtraRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      api.post(`${base(businessId)}/sessions/${sessionId}/extras`, input, addSessionExtraResponseSchema, { idempotencyKey: newIdempotencyKey() }),
    // Also on an error: the add can be saved with its answer lost (a timeout), and a retry is then refused.
    onSettled: () => refreshExtras(queryClient, businessId),
  });
}

export function useRemoveSessionExtra(businessId: string, sessionId: string): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (extraId) => api.delete(`${base(businessId)}/sessions/${sessionId}/extras/${extraId}`),
    onSuccess: () => refreshExtras(queryClient, businessId),
  });
}

/** GET .../session-extras: the sessions the owner decides. */
export function useOwnerSessionExtras(businessId: string | null): UseQueryResult<OwnerSessionExtrasResponse> {
  return useQuery({
    queryKey: [...queryKeys.coachParticipants.all(businessId ?? ""), "session-extras"],
    queryFn: () => api.get(`${base(businessId!)}/session-extras`, ownerSessionExtrasResponseSchema),
    enabled: Boolean(businessId),
  });
}

export function useDecideSessionExtra(businessId: string): UseMutationResult<void, unknown, DecideSessionExtraRequest & { id: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }) => {
      await api.post(`${base(businessId)}/session-extras/${id}/decide`, input, okResponseSchema, { idempotencyKey: newIdempotencyKey() });
    },
    onSuccess: () => {
      refreshExtras(queryClient, businessId);
      void queryClient.invalidateQueries({ queryKey: queryKeys.owner.members(businessId) });
    },
  });
}

/** GET .../came-during-pause: Came took a session from a paused package (2026-10-29). */
export function useCameDuringPause(businessId: string | null): UseQueryResult<CameDuringPauseResponse> {
  return useQuery({
    queryKey: [...queryKeys.coachParticipants.all(businessId ?? ""), "came-during-pause"],
    queryFn: () => api.get(`${base(businessId!)}/came-during-pause`, cameDuringPauseResponseSchema),
    enabled: Boolean(businessId),
  });
}

/** End the pause today, or keep it. */
export function useAnswerCameDuringPause(businessId: string): UseMutationResult<void, unknown, AnswerCameDuringPauseRequest & { id: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }) => {
      await api.post(`${base(businessId)}/came-during-pause/${id}`, input, okResponseSchema, { idempotencyKey: newIdempotencyKey() });
    },
    onSuccess: () => {
      refreshExtras(queryClient, businessId);
      void queryClient.invalidateQueries({ queryKey: queryKeys.owner.members(businessId) });
    },
  });
}

// --- Ask the parents (migration 0045) -------------------------------------------------------------

/** The owner copied the group message (`post`) or the reminder. */
export function useAskParents(businessId: string): UseMutationResult<{ findUrl: string }, unknown, "post" | "reminder"> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (kind) => api.post(`${base(businessId)}/new-participants/ask-parents`, { kind }, askParentsResponseSchema),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.coachParticipants.owner(businessId) }),
  });
}

/** Link an answer: its names go to the person who answered. */
export function useLinkParentClaim(
  businessId: string,
): UseMutationResult<{ linked: number; linkedNames: string[]; skippedNames: string[]; familyId: string | null; secondAdult: boolean }, unknown, { id: string; weekdays: Record<string, number[]> }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, weekdays }) => api.post(`${base(businessId)}/new-participants/claims/${id}/link`, { weekdays }, linkParentClaimResponseSchema),
    // Also after an error: a link can commit while its answer is lost, and the page must not keep
    // showing the answer as open (review F6.2).
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.coachParticipants.all(businessId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.setup.all(businessId) });
    },
  });
}

/** No to an answer. */
export function useRejectParentClaim(businessId: string): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      await api.post(`${base(businessId)}/new-participants/claims/${id}/reject`, {}, okResponseSchema);
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.coachParticipants.owner(businessId) }),
  });
}

/** The find page for the signed-in person: the business, and whether this number is in its group. */
export function useFindPage(code: string): UseQueryResult<FindPage> {
  return useQuery({ queryKey: queryKeys.find.page(code), queryFn: () => api.get(`/find/${encodeURIComponent(code)}`, findPageSchema), retry: false });
}

/** Children whose name matches `q` by the name search rule, one word on the first name (2 letters at least). */
export function useFindChildren(code: string, q: string, enabled: boolean): UseQueryResult<{ items: FindChild[]; linkedElsewhere: boolean; linkedToYou: boolean }> {
  const typed = q.trim();
  return useQuery({
    queryKey: queryKeys.find.children(code, typed.toLowerCase()),
    queryFn: () => api.get(`/find/${encodeURIComponent(code)}/children?q=${encodeURIComponent(typed)}`, findChildrenResponseSchema),
    // No placeholder: a result of an earlier query must never be picked under a new one (review F1.3).
    enabled: enabled && typed.length >= 2,
  });
}

/** "These are my children": the answer goes to the business. */
export function useSendFindClaim(code: string): UseMutationResult<{ id: string }, unknown, FindClaimRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post(`/find/${encodeURIComponent(code)}/claims`, body, findClaimResponseSchema),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.find.page(code) }),
  });
}

// --- Ask the coach on WhatsApp (migration 0046) ---------------------------------------------------

/** The owner asks the coach of these names who the parent is. */
export function useAskCoach(
  businessId: string,
): UseMutationResult<{ id: string; coachName: string; whatsappStatus: "sent" | "failed" | "no_phone" | "not_configured" }, unknown, { pendingIds: string[]; coachUserId: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post(`${base(businessId)}/new-participants/coach-asks`, body, askCoachResponseSchema),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.coachParticipants.owner(businessId) }),
  });
}

/** The coach's "Parent wanted" questions. */
export function useStaffParentWanted(businessId: string | null): UseQueryResult<StaffParentWantedResponse> {
  return useQuery({
    queryKey: [...queryKeys.coachParticipants.mine(businessId ?? "none"), "parent-wanted"],
    queryFn: () => api.get(`${base(businessId ?? "")}/staff/parent-wanted`, staffParentWantedResponseSchema),
    enabled: Boolean(businessId),
  });
}

/** The coach answers one question. */
export function useAnswerParentWanted(businessId: string): UseMutationResult<void, unknown, { id: string } & AnswerParentWantedRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }) => {
      await api.post(`${base(businessId)}/staff/parent-wanted/${id}/answer`, body, okResponseSchema);
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.coachParticipants.mine(businessId) }),
  });
}
